import React from 'react';
import { ModuleKey } from './entitlement';

/**
 * Inline banner shown where a gated module would have rendered.
 * Replaces the hidden page/block with a call-to-action to upgrade —
 * plan §8 "ModuleDisabledBanner (402 aware)".
 *
 * The banner is deliberately neutral (no scary red): for Phase 1–5
 * it never actually renders because `TARIFF_GATING_ENABLED` is off;
 * for Phase 6+ shops that legitimately lack a module it should read
 * as an upsell, not an error.
 */

interface ModuleDisabledBannerProps {
  module: ModuleKey;
  /** Optional context — e.g. "Competitor analysis". */
  title?: string;
  /** Optional 1–2 sentence explanation of what this module unlocks. */
  description?: string;
  /** Override the default `/billing` CTA target. */
  upgradeUrl?: string;
}

const MODULE_LABELS: Record<ModuleKey, string> = {
  base: 'Базовый модуль',
  supply: 'Модуль «Поставки»',
  ai: 'AI-модуль',
  spp: 'SPP / Конкуренты',
};

const DEFAULT_DESCRIPTIONS: Record<ModuleKey, string> = {
  base: 'Этот модуль входит в любой тариф.',
  supply:
    'Управление поставками Ozon доступно на тарифах с модулем «Поставки».',
  ai: 'AI-рекомендации доступны на тарифах Pro и Premium.',
  spp: 'Анализ конкурентов доступен на тарифе Premium.',
};

export function ModuleDisabledBanner({
  module,
  title,
  description,
  upgradeUrl = '#billing',
}: ModuleDisabledBannerProps) {
  return (
    <div
      role="region"
      aria-label="module-disabled"
      style={{
        border: '1px solid #cbd5e1',
        background: '#f8fafc',
        borderRadius: 12,
        padding: 20,
        margin: '16px 0',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ fontSize: 16, fontWeight: 600, color: '#0f172a' }}>
        {title ?? MODULE_LABELS[module]}
      </div>
      <div style={{ fontSize: 14, color: '#475569', lineHeight: 1.5 }}>
        {description ?? DEFAULT_DESCRIPTIONS[module]}
      </div>
      <div>
        <a
          href={upgradeUrl}
          style={{
            display: 'inline-block',
            padding: '8px 16px',
            background: '#2b6eff',
            color: '#fff',
            borderRadius: 8,
            fontWeight: 600,
            textDecoration: 'none',
            fontSize: 14,
          }}
        >
          Посмотреть тарифы
        </a>
      </div>
    </div>
  );
}
